import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useQuery } from "convex/react";
import { useAtomValue } from "jotai";
import { Building2 } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { api as cyranoApi } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import { useIsMobile } from "@/core/hooks";
import { activeAccountScopeAtom } from "@/core/state";
import { commissionOfGross } from "@/shared/creneaux";
import { Avatar, Etiquette, Infobulle } from "@/shared/ui";

/**
 * Chip header : icône + nom organisation + 3 stats commission du user courant
 * (mes 30 derniers jours). Affiché à GAUCHE du UserBadge dans la TitleBar.
 *
 * Stats lues via GET /api/sales-attribution/me (backend Cyrano — déjà
 * scope sur session.workos_user_id et conversion EUR/GBP/CAD/AUD → USD via
 * CASE SQL). Refetch toutes les 60s pour rester en phase avec les
 * paiements Dropp temps-réel.
 *
 * 3 valeurs affichées :
 *   - Commission % du membre (accountMembers.salesCommissionPercent, Convex)
 *   - Brut généré sur 30j (sum des ventes attribuées en USD)
 *   - Net en attente = brut × NET_REVENUE_MULTIPLIER × commission %, calculé ICI
 *     — au % du créneau pour les ventes faites dans un créneau payé
 *     (`shiftCommissionRates`, 30-09-2026), sur `gross_by_half_hour` de `/me`
 *
 * 🩸 Le Net lisait `commission_due_cents` du backend jusqu'au 23-08-2026 : il
 * valait « $0 » pour TOUT LE MONDE, en permanence, parce que le snapshot % de
 * `Order` n'a jamais été écrit (0 valeur sur 5246 commandes). Le % vivant vient
 * de Convex, pas de Neon — cf `@/shared/commission`. Ne pas revenir en arrière.
 *
 * Sur mobile, on collapse à l'icône + nom org uniquement (pas les stats —
 * la titlebar n'a pas la place). L'employé peut toujours voir le détail dans
 * son onglet Team → membre.
 *
 * Le composant retourne null s'il n'y a pas d'account (cas bootstrap user
 * sans org) pour ne pas casser la TitleBar.
 */
export function OrgAndStatsBadge() {
  const { t } = useTranslation();
  const { user } = useSession();
  const isMobile = useIsMobile();
  const accounts = useQuery(api.accounts.listMine);
  const scope = useAtomValue(activeAccountScopeAtom);
  // L'org affichée suit le sélecteur de scope cross-org (AccountScopeSwitcher),
  // pas un `accounts[0]` figé : sinon le badge garde le nom de la 1re org même
  // après sélection d'une autre (bug rapporté 2026-06-18 : sélection "Xeyla"
  // mais badge affiche toujours "la marmite").
  //   - single + compte encore membre        → ce compte
  //   - all (vue agrégée multi-org)           → libellé dédié, pas de compte unique
  //   - sinon (null / legacy / compte quitté) → 1er compte (mono-org)
  const isAllScope = scope?.kind === "all";
  const scopedAccount =
    scope?.kind === "single"
      ? accounts?.find((a) => a._id === scope.accountId) ?? null
      : null;
  const activeAccount =
    scopedAccount ?? (isAllScope ? null : accounts?.[0] ?? null);
  const myPerms = useQuery(
    api.members.getMyPermissionsForAccount,
    activeAccount ? { accountId: activeAccount._id } : "skip",
  );
  const myDetail = useQuery(
    api.members.getMemberDetail,
    myPerms?.membershipId ? { membershipId: myPerms.membershipId } : "skip",
  );

  // Stats personnelles via backend Cyrano. Pas de query params from/to —
  // l'endpoint /me prend une fenêtre fixe de N jours (defaut 30).
  // Les créneaux payés de l'org (Kayla, 30-09-2026) : une vente faite dans un
  // créneau est payée au % du créneau. Vue agrégée « toutes les orgs » : pas
  // d'org, donc ni % ni créneaux — « — ».
  const slots = useQuery(
    api.shiftCommissionRates.listForAccount,
    activeAccount ? { accountId: activeAccount._id } : "skip",
  );

  const { data: stats } = useReactQuery({
    queryKey: ["sales-attribution-me", user?.workosUserId ?? "anon"],
    queryFn: async (): Promise<{
      workos_user_id: string;
      sales_count: number;
      gross_revenue_cents: number;
      /** 48 demi-heures murales de Paris. Absent = backend d'avant. */
      gross_by_half_hour?: number[];
    }> => cyranoApi.get("/api/sales-attribution/me?days=30"),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Pas d'account résolu ET pas en vue agrégée → on ne rend rien (TitleBar
  // reste OK avec juste UserBadge).
  if (!activeAccount && !isAllScope) return null;

  const orgName = isAllScope
    ? t("shell.accountScope.all")
    : activeAccount!.name;
  const orgLogo = isAllScope ? null : activeAccount!.logoUrl;
  const commissionPct = myDetail?.salesCommissionPercent ?? null;
  const grossUsd = stats?.gross_revenue_cents ?? 0;
  // `null` tant que Convex n'a pas rendu le % ou les créneaux, si le membre
  // n'a pas de %, ou si des créneaux existent sans les demi-heures du
  // backend : on affiche « — », jamais « $0 » ni un Net sous-évalué (un zéro
  // se lit comme « tu n'as rien gagné »).
  const commissionFloat = stats
    ? commissionOfGross({
        grossCents: grossUsd,
        halfHours: stats.gross_by_half_hour,
        basePercent: commissionPct,
        slots,
      })
    : null;
  const commissionUsd =
    commissionFloat === null ? null : Math.round(commissionFloat);

  // Layout :
  //   - Desktop : [logo] [org] │ Commission 10% │ Brut $1200 │ Net $120
  //   - Mobile  : [logo] [org] │ Net $120
  // Decision UX 2026-05-27 : sur mobile, on garde uniquement Net car la
  // titlebar est trop etroite pour 3 stats des qu'un chiffre depasse 3
  // digits ou que le nom d'agence depasse "Xeyla". L'employe peut voir
  // commission % + brut dans le detail Team → membre. Net en titlebar =
  // ce qui motive vraiment au quotidien (= argent dans la poche).
  return (
    <div
      className={
        // Chip SANS contour : il s'inscrit directement dans le verre dépoli de
        // la TitleBar (fond d'écran flouté). Le cadre rectangulaire a été retiré
        // (demande user : enlever les contours autour du profil). Pas de padding
        // horizontal non plus → l'org colle au reste du contenu inline.
        "flex h-6 items-center text-[11px] text-fg " +
        (isMobile ? "gap-1.5" : "gap-2")
      }
    >
      {/* Org icon + nom — DESKTOP uniquement. Sur mobile (demande user
          2026-06-30) on ne garde que le solde : la titlebar est trop étroite
          et le nom d'org n'apporte rien au quotidien (une seule org). */}
      {!isMobile && (
        <>
          <div className="flex items-center gap-1.5">
            <Avatar src={orgLogo} icone={<Building2 className="h-3 w-3" />} taille={20} />
            <span className="max-w-[8rem] truncate font-medium">{orgName}</span>
          </div>

          <span aria-hidden className="h-3 w-px bg-fg/10" />
        </>
      )}

      <Infobulle texte={t("shell.userBadge.statsTooltip")} cote="bas">
        <div
          className={
            "flex items-center tabular-nums " + (isMobile ? "gap-1.5" : "gap-2")
          }
        >
          {/* Commission % + Brut : desktop uniquement (cf decision UX ci-dessus). */}
          {!isMobile && (
            <>
              <Stat
                label={t("shell.userBadge.commissionLabel")}
                value={
                  commissionPct === null
                    ? "—"
                    : `${formatPercent(commissionPct)}%`
                }
              />
              <span aria-hidden className="h-3 w-px bg-fg/10" />
              <Stat
                label={t("shell.userBadge.grossLabel")}
                value={formatUsd(grossUsd)}
              />
              <span aria-hidden className="h-3 w-px bg-fg/10" />
            </>
          )}
          {/* Net : toujours visible (desktop + mobile). C'est la stat qui
              motive l'employe au quotidien — argent dans la poche. */}
          <Stat
            label={t("shell.userBadge.netLabel")}
            value={commissionUsd === null ? "—" : formatUsd(commissionUsd)}
            highlight
          />
        </div>
      </Infobulle>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  /** Texte de label uppercase. Si `null` (cas mobile pour la commission),
   *  on n'affiche que la valeur — utilise quand le suffixe sur la valeur
   *  (ex: "%") rend le label redondant. */
  label: string | null;
  value: string;
  highlight?: boolean;
}) {
  return (
    <span className="flex items-baseline gap-1">
      {label !== null && (
        <Etiquette>{label}</Etiquette>
      )}
      <span
        className={
          highlight ? "font-semibold text-fg" : "font-medium text-fg"
        }
      >
        {value}
      </span>
    </span>
  );
}

/** Format cents (USD, post-conversion CASE SQL côté backend) → "$1,234".
 *  Arrondi à l'entier — la précision décimale n'apporte rien dans un header
 *  permanent (le détail est dans Team → member). */
function formatUsd(cents: number): string {
  if (!Number.isFinite(cents) || cents === 0) return "$0";
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/** Format % avec au plus 1 décimale — 10 → "10", 12.5 → "12.5". */
function formatPercent(pct: number): string {
  return Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
}
